import React, { useEffect, useState } from 'react';
import { Bell, Menu, LogOut, ChevronDown, Moon, Sun, Volume2, VolumeX } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { notificationApi } from '@/services/notificationApi';
import { audioPlayer } from '@/utils/audioPlayer';

interface NavbarProps {
  onSidebarToggle?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onSidebarToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSoundMuted, setIsSoundMuted] = useState(audioPlayer.isSoundMuted());
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Dark mode handler
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const isDark = saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);
    if (nextDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!user || (!['admin', 'super_admin'].includes(user.role || '') && user.role !== 'vendor')) {
        setUnreadCount(0);
        return;
      }

      try {
        const count = await notificationApi.getUnreadCount(['admin', 'super_admin'].includes(user.role || '') ? 'admin' : 'vendor');
        setUnreadCount(count);
      } catch {
        setUnreadCount(0);
      }
    };

    loadUnreadCount();

    const handleChanged = () => {
      loadUnreadCount();
    };

    window.addEventListener('notifications:changed', handleChanged);
    return () => {
      window.removeEventListener('notifications:changed', handleChanged);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    setShowUserMenu(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationsClick = () => {
    if (user?.role === 'vendor') {
      navigate('/vendor/notifications');
      return;
    }

    navigate('/admin/notifications');
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 w-full items-center justify-between border-b border-slate-200/80 bg-white/90 px-3 sm:px-5 lg:px-6 backdrop-blur-md dark:border-slate-800/80 dark:bg-slate-900/90 transition-colors">
      {/* Left: Sidebar Toggle & Page Info */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        <button
          onClick={onSidebarToggle}
          className="flex items-center justify-center min-w-[42px] min-h-[42px] rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
          aria-label="Menüyü aç/kapat"
        >
          <Menu size={22} />
        </button>

        <div className="hidden sm:block">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            TazeBiJest
          </span>
          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
            {['admin', 'super_admin'].includes(user?.role || '') ? 'Yönetim Paneli' : 'Vendor Paneli'}
          </h2>
        </div>
      </div>

      {/* Right: Sound Toggle, Theme Toggle, Notifications, User Profile */}
      <div className="flex items-center gap-1.5 sm:gap-3">
        {/* Order Sound Alert Toggle */}
        <button
          onClick={() => {
            const isMutedNow = audioPlayer.toggleSoundMuted();
            setIsSoundMuted(isMutedNow);
          }}
          className="flex items-center justify-center min-w-[40px] min-h-[40px] rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
          title={isSoundMuted ? 'Sipariş Bildirim Sesi Kapalı (Açmak için tıklayın)' : 'Sipariş Bildirim Sesi Açık (Kapatmak için tıklayın)'}
          aria-label="Sipariş sesi ayarı"
        >
          {isSoundMuted ? (
            <VolumeX size={20} className="text-rose-400" />
          ) : (
            <Volume2 size={20} className="text-emerald-500 animate-pulse" />
          )}
        </button>

        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="flex items-center justify-center min-w-[40px] min-h-[40px] rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
          title={isDarkMode ? 'Açık Temaya Geç' : 'Koyu Temaya Geç'}
          aria-label="Tema değiştir"
        >
          {isDarkMode ? (
            <Sun size={20} className="text-amber-400" />
          ) : (
            <Moon size={20} className="text-slate-600" />
          )}
        </button>

        {/* Notifications */}
        <button
          onClick={handleNotificationsClick}
          className="relative flex items-center justify-center min-w-[40px] min-h-[40px] rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
          aria-label="Bildirimler"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center px-1 border-2 border-white dark:border-slate-900 animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu((current) => !current)}
            className="flex items-center gap-2.5 rounded-xl p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-[42px]"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-500 text-sm font-bold text-white shadow-sm flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="hidden text-left sm:block pr-1">
              <p className="text-xs font-bold text-slate-800 dark:text-slate-100 leading-snug">
                {user?.name || 'Admin'}
              </p>
              <span className="inline-block text-[10px] font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-950/60 px-1.5 py-0.2 rounded border border-brand-200/50 dark:border-brand-800/50">
                {user?.role === 'super_admin' ? 'Süper Admin' : (user?.role === 'admin' ? 'Yönetici' : 'Vendor')}
              </span>
            </div>
            <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
          </button>

          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 mt-2 z-50 w-56 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl animate-fade-in">
                <div className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 px-4 py-3">
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {user?.email}
                  </p>
                </div>

                <div className="p-1">
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
